import { useNavigate } from 'react-router-dom';
import { IconBack } from '../components/Icon';
import { useSession } from '../lib/session';
import AdminRoster from '../components/AdminRoster';
import { MarkWhistle } from '../components/Icon';

/**
 * The organiser's one screen: who is invited, who has got in, and who is on
 * what.
 *
 * **It used to be two, and they were asking the same question twice.** A
 * "Guests" list of every account sat under the roster survey, and between them
 * a guest appeared once as an invited name and again as a signed-in address,
 * with the team, the display name and the email repeated on both. On a phone
 * that is the same person twice, forty rows apart.
 *
 * So the roster absorbed it. Everything the guest list carried that the survey
 * did not — the organiser chip, the upload count, when they were last seen, and
 * the team control from D54 — moved onto the roster card, which already knew
 * who held each row.
 *
 * **What that trades away, and the first answer to it was wrong.** Every card
 * on the survey is a roster row, so an account tied to none of them has no
 * card. The reasoning here used to be that this only caught somebody who
 * "signed in, did not claim, and did not ask for help" — stuck and silent, and
 * rare.
 *
 * That missed the two ways it happens in normal use. Unmatch is a button on
 * every claimed card, and self-release is a supported guest action (F2,
 * explicitly unbounded). Both put an account in exactly that state the moment
 * they are used — so the tool an organiser reaches for to settle a dispute was
 * making its own subject disappear from the screen they were settling it on,
 * with no abandonment required.
 *
 * So the roster names them: `AdminRosterResponse.unlinked`, rendered under the
 * survey. Getting one a row back still goes through the claim-requests queue,
 * which needs the guest to ask — but an organiser who cannot see the account
 * has no way of knowing that is what is needed.
 *
 * This file is now a shell: the guard, the way back, and the roster. It keeps
 * its route (`/me/guests`) because that is the link an organiser has already
 * bookmarked.
 */
export default function AdminGuests() {
  const session = useSession();
  const navigate = useNavigate();

  // `session &&` on purpose: `undefined` is "not asked yet" and must not render
  // the refusal, or a returning organiser sees "that page is for organisers"
  // for a frame before their own profile answers.
  if (session && !session.isAdmin) {
    return (
      <div className="page page-pad empty" style={{ minHeight: '60dvh', justifyContent: 'center' }}>
        <MarkWhistle className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Not this one</h4>
        <p>That page is for organisers.</p>
      </div>
    );
  }

  return (
    <div className="page page-narrow page-narrow-lg" style={{ padding: '10px 20px 96px' }}>
      <button type="button" className="btn btn-secondary" style={{ minHeight: 42, gap: 8 }} onClick={() => navigate('/me')}>
        <IconBack size={16} /> Profile
      </button>

      <AdminRoster />

      <p style={{ fontSize: 11, opacity: 0.5, marginTop: 18 }}>
        {/* Saying "seen" rather than "last login" is not pedantry: the
            timestamp updates on silent token refreshes too, so it tracks use
            rather than sign-in. Calling it a login would be a lie an organiser
            might act on. */}
        "Seen" is the last time someone had the app open, not the last time they signed in.
        Times are Khao Lak time.
      </p>
    </div>
  );
}
